// ts-rest contract for the Desk per-page typed endpoints
// (client-knowledge, calls-meetings). Replaces useTable + named-action
// dispatches for the Desk surface.
//
// Per ADR-0003 the response rows the server knows the shape of are real
// `z.object` schemas, not `z.record(z.unknown())` stand-ins. The genuinely-
// loose populations that survive are named at their use sites below.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
    knowledgeAssignmentTargetTypeSchema,
    knowledgeItemTypeSchema,
    staffActionErrorSchema,
} from './agent';
import { clientStubSchema } from '../schemas/clients';

const c = initContract();

import { errorBody } from '../schemas/common';

// --- Client Knowledge KB (per-client "what we know") ------------------------
// The Client Knowledge page reads its picker from the canonical `Client`
// ontology object (one entry per firm Client), and a per-client knowledge base
// composed from the REAL ATLAS call-extracted artefacts — `call_kb_notes`
// (markdown KB notes) and `call_extracted_facts` (categorised facts). Each KB
// is keyed by the `Client.id`, so `deskKnowledgeBases[clientId]` == the picker
// id == the artefacts' `clientId`. Presentation projection (ADR-0003) of the
// CallKbNote / CallExtractedFact artefacts; the page renders these shapes
// directly (mirrors apps/web/src/data/types.ts `DeskKBNote` / `DeskKBGraphNode`
// / `DeskClientKB`).

/**
 * One ACTIVE reference from a knowledge entry to a downstream record, as the
 * Client Knowledge page renders it (SEP-183 / ADR-0015). A projection of a
 * `KnowledgeAssignment` row.
 *
 * The item stays owned by its Contact; the record only points at it. An ended
 * reference never reaches the wire, because the page shows what applies NOW.
 * The audit trail keeps the ended row, which is where history is read.
 */
export const knowledgeAssignmentRowSchema = z.object({
    id: z.string(),
    targetType: knowledgeAssignmentTargetTypeSchema,
    targetId: z.string(),
    /** What the reader sees. The server resolves it from the target tier, and
     *  falls back to the tier label when the target is unreadable. */
    targetLabel: z.string(),
    assignedAtLabel: z.string(),
});
export type KnowledgeAssignmentRow = z.infer<typeof knowledgeAssignmentRowSchema>;

/** A KB note as the Client Knowledge page renders it (a `CallKbNote` row, or a
 *  single `CallExtractedFact` projected as a note-like entry). */
export const deskKBNoteSchema = z.object({
    id: z.string(),
    /** Which knowledge type `id` names. The page renders a `CallKbNote` and a
     *  `CallExtractedFact` the same way, so only this field tells the assign
     *  control which door to knock on (SEP-183). */
    itemType: knowledgeItemTypeSchema,
    /** The records this entry applies to now. Empty means the entry stays at
     *  person level, which is the ADR-0015 default. */
    assignments: z.array(knowledgeAssignmentRowSchema),
    title: z.string(),
    content: z.string(),
    template: z.string(),
    tags: z.array(z.string()),
    dateModifiedLabel: z.string(),
    linkedNotes: z.array(z.string()),
    emailProvenance: z.string().nullable(),
    agentGenerated: z.boolean().optional(),
    // The call this note was drafted from, when it is an ATLAS artefact —
    // resolves the `[^N]` citations in `content` against the call transcript.
    callId: z.string().nullable().optional(),
});
export type DeskKBNote = z.infer<typeof deskKBNoteSchema>;

/** An entity node in the Client Knowledge relationship graph. `kind` drives the
 *  rendering: the central `contact` (the PERSON the graph is built out of —
 *  SEP-205), a `portfolio` they hold, a connected `person` (navigable when
 *  `linkedContactId` resolves), or a life-`event` milestone. The graph lays out
 *  radially client-side, so no x/y here.
 *
 *  There is no `client` kind. The centre is always a Contact (SEP-215): a person
 *  on this graph is always a real Contact row. */
export const deskKBGraphNodeSchema = z.object({
    id: z.string(),
    kind: z.enum(['contact', 'portfolio', 'person', 'event']),
    label: z.string(),
    // person: the relationship to the centre (display label).
    // portfolio: the wrapper type, e.g. "ISA" or "SIPP".
    relationship: z.string().nullable(),
    // person: the connected person's own Client id when they are in the book —
    // the node becomes a link to /desk/client-book/:linkedClientId. Else null.
    linkedClientId: z.string().nullable(),
    // person: their own Contact id when they are in the directory — the node
    // becomes a link to /desk/contacts/:linkedContactId. Preferred over
    // linkedClientId, which points at an Account rather than a person.
    linkedContactId: z.string().nullable().optional(),
    // person: source note/quote; event: the verbatim call quote;
    // portfolio: the Tercero account reference.
    detail: z.string().nullable(),
    // event: a British date label for the milestone.
    date: z.string().nullable(),
    /** `recorded` — a real Relationship, account or approved artefact.
     *  `proposed` — a PENDING RelationshipProposal that no human has decided.
     *  The graph draws a proposed node dashed (SEP-215). */
    state: z.enum(['recorded', 'proposed']),
    /** The pending RelationshipProposal behind a `proposed` node, so the node
     *  links through to its entry in the agent review queue. Null otherwise. */
    proposalId: z.string().nullable(),
});
export type DeskKBGraphNode = z.infer<typeof deskKBGraphNodeSchema>;

/** A directed edge in the Client Knowledge graph. The centre-to-satellite
 *  spokes and the satellite-to-satellite edges use the same shape. */
export const deskKBGraphEdgeSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    label: z.string().nullable(),
});
export type DeskKBGraphEdge = z.infer<typeof deskKBGraphEdgeSchema>;

export const deskClientKBSchema = z.object({
    /** The Contact id this knowledge base belongs to (SEP-205). */
    id: z.string(),
    clientName: z.string(),
    /**
     * The Client row this person reaches, when exactly one is resolvable. It
     * feeds the PORTFOLIO nodes only: an Account is a Client-tier fact
     * (ADR-0015 K4). It gates no write path any more — every knowledge write
     * keys on the Contact since SEP-215. Null when the contact resolves to no
     * Client, which about a third of the book does.
     */
    primaryClientId: z.string().nullable(),
    notes: z.array(deskKBNoteSchema),
    graphNodes: z.array(deskKBGraphNodeSchema),
    graphEdges: z.array(deskKBGraphEdgeSchema),
    lastUpdatedLabel: z.string(),
});
export type DeskClientKB = z.infer<typeof deskClientKBSchema>;

/** A proposed person-to-person relationship, as the Client Knowledge graph
 *  renders it in a list (the manual-add echo). It mirrors the display fields of
 *  a `RelationshipProposal` row.
 *
 *  `proposedRole` is a RelationshipRole catalogue key, `z.string()` on the wire:
 *  the documented open-enum exception (ADR-0015 D4). The write boundary
 *  validates the key, and behavioural code reads catalogue flags. The old closed
 *  `clientConnectionRelationshipSchema` enum is gone with SEP-215.
 *
 *  There is no counterparty field. A proposal names a person; the reviewer binds
 *  the counterparty Contact at approval, and the approval writes a real
 *  `Relationship`. */
export const relationshipProposalSchema = z.object({
    id: z.string(),
    /** The subject person. Null when the call resolved to no Contact. */
    contactId: z.string().nullable(),
    personName: z.string(),
    proposedRole: z.string(),
    proposedRoleLabel: z.string().nullable(),
    note: z.string().nullable(),
    source: z.enum(['atlas', 'manual']),
    reviewStatus: z.enum(['pending', 'approved', 'rejected', 'revision_requested']).nullable(),
    /** The Relationship an approval produced, or null while the proposal waits. */
    promotedRelationshipId: z.string().nullable(),
});
export type RelationshipProposalDto = z.infer<typeof relationshipProposalSchema>;

/** A Markdown document an IM attached by hand to a person's knowledge base
 *  (a `ClientKnowledgeDocument` row). Distinct from the ATLAS artefacts: it is
 *  human-authored Markdown and is removable by its IM. */
export const clientKnowledgeDocumentSchema = z.object({
    id: z.string(),
    /** The subject person — the key the page groups on (SEP-215). */
    contactId: z.string().nullable(),
    /** Legacy provenance: the Client the document was attached to. It keys
     *  nothing. */
    clientId: z.string().nullable(),
    title: z.string(),
    markdownContent: z.string(),
    createdByUserId: z.string(),
    createdAt: z.string(),
    createdAtLabel: z.string(),
    sourceFilename: z.string().nullable(),
    /** The records this document applies to now (SEP-183). A document is a
     *  knowledge base entry, so it carries the same references an ATLAS
     *  artefact does. A freshly created document carries none. */
    assignments: z.array(knowledgeAssignmentRowSchema),
});
export type ClientKnowledgeDocumentDto = z.infer<typeof clientKnowledgeDocumentSchema>;

export const deskClientKnowledgeResponseSchema = z.object({
    clients: z.array(clientStubSchema),
    // Per-person knowledge bases, keyed by `Contact.id` (SEP-205 / SEP-215).
    // The key was never `Client.id`, and the comment that said so was wrong. A
    // person with no ATLAS artefacts is absent from the map, and the page reads
    // a missing entry as an empty knowledge base.
    deskKnowledgeBases: z.record(deskClientKBSchema),
    // Per-person IM-authored Markdown documents (ClientKnowledgeDocument rows),
    // keyed by `Contact.id`. Soft-deleted documents are excluded. A person with
    // no manual documents is absent from the map.
    clientKnowledgeDocuments: z.record(z.array(clientKnowledgeDocumentSchema)),
    // The contact of each Client that a contact bridges to, keyed by
    // `Client.id`. A deep link that names a Client opens the contact through it.
    contactIdByClientId: z.record(z.string()),
});
export type DeskClientKnowledgeResponse = z.infer<typeof deskClientKnowledgeResponseSchema>;

// --- The two knowledge bands on a record (SEP-222 / ADR-0015) ---------------
// A knowledge item is owned by a PERSON and never by a record, so a record has
// no knowledge of its own to list. It has two different lists instead. The
// reader must be able to tell them apart, because one is inherited and the
// other is a decision somebody made.

/** One knowledge entry as a record's knowledge tab renders it. */
export const recordKnowledgeItemSchema = z.object({
    itemType: knowledgeItemTypeSchema,
    itemId: z.string(),
    title: z.string(),
    content: z.string(),
    /** The entry kind as a reader's label, e.g. "Meeting Notes". */
    template: z.string(),
    tags: z.array(z.string()),
    dateModifiedLabel: z.string(),
    /** The person who owns the entry. Null when the call resolved to nobody. */
    contactId: z.string().nullable(),
    contactName: z.string().nullable(),
    /** The assignment that puts this entry in the "About this record" band, so
     *  the reader can end it. Null for every entry in the person band. */
    assignmentId: z.string().nullable(),
});
export type RecordKnowledgeItem = z.infer<typeof recordKnowledgeItemSchema>;

export const recordKnowledgeResponseSchema = z.object({
    /** What the record is called, for the band headings. */
    recordLabel: z.string(),
    /** The people the record relates to. Empty is a real state: a record with
     *  no active person relationship inherits nothing, and the tab says so. */
    people: z.array(z.object({ contactId: z.string(), contactName: z.string() })),
    /** About this person — inherited from the related Contacts. An entry that
     *  the record already carries is excluded, so no entry reads twice. */
    aboutPerson: z.array(recordKnowledgeItemSchema),
    /** About this record — an active KnowledgeAssignment points at it. */
    aboutRecord: z.array(recordKnowledgeItemSchema),
});
export type RecordKnowledgeResponse = z.infer<typeof recordKnowledgeResponseSchema>;

// --- Calls & Meetings -------------------------------------------------------
// The composed payload draws from four firm-infrastructure tables. Each row is
// projected to its honest DTO (camelCase wire keys mirror the Drizzle columns).

/** A logged call (one `desk_calls` row). */
export const deskCallRowSchema = z.object({
    id: z.string(),
    clientId: z.string().nullable(),
    status: z.string().nullable(),
    // Display fields projected from the desk_calls row (Numonix → transcription →
    // DeskCall). Nullable/defaulted so a freshly-ingested call (before ATLAS
    // scores sentiment/summary) still renders cleanly.
    clientName: z.string().nullable(),
    // The person on the telephone (SEP-182 / ADR-0015 K4), resolved against the
    // Contact tier's three phone columns and STORED on the call at ingest. One
    // Contact answers for many Accounts — trusts, family accounts — so
    // this is the name a call row is labelled with; `clientName` remains for the
    // unresolved third of the book and for anything keyed on the Account.
    contactId: z.string().nullable(),
    contactName: z.string().nullable(),
    // The colleague at the FIRM end of the recorded leg, by NAME (SEP-260).
    // The stored `handled_by_user_id` never goes on the wire, but the name does:
    // a reader who can open the call already reads the conversation, and the
    // call-notes PDF has to print the investment manager who was ON the call
    // rather than whichever colleague pressed Download. Null when the firm end
    // resolved to no Active `users` row.
    handledByName: z.string().nullable(),
    // Which `transcript[].speaker` label is the FIRM side (SEP-260), for example
    // "Adviser". The labels come from the audio channel and are frequently the
    // wrong way round; ATLAS reads the words and names the side, and the server
    // stores its answer. Null means unconfirmed, and the interface then shows
    // the two parties as roles rather than as named people.
    firmSpeakerLabel: z.string().nullable(),
    // WHO said so. `atlas` is the agent's reading of the words; `user` is a
    // person who was on the call, or who knows who was, correcting it. Null when
    // nobody has named the side at all.
    //
    // The distinction is not decoration. A `user` answer is final: ATLAS must
    // never overwrite it on a later run, and the interface says whose answer the
    // reader is looking at, because "the manager told us" and "the agent thinks"
    // do not deserve equal trust.
    firmSpeakerSource: z.enum(['atlas', 'user']).nullable(),
    // The colleague who corrected the sides, by name, and when. Both null unless
    // `firmSpeakerSource` is `user`. The id stays off the wire, as every other
    // staff id on this row does.
    firmSpeakerSetByName: z.string().nullable(),
    firmSpeakerSetAt: z.string().nullable(),
    callType: z.string().nullable(),
    // True when the call's external participant is a Dowgate colleague (their
    // email is on the firm domain) — an internal call, not a client interaction.
    // Derived server-side from the raw participant email (which never ships on
    // the wire); drives the "Exclude internal calls" filter (SEP-158).
    isInternal: z.boolean(),
    callKind: z.enum(['fact_find', 'regular']).nullable(),
    // Which door the call came through (SEP-259). `numonix` is the recorder, and
    // `upload` is a recording a person dragged in and assigned by hand. The page
    // labels the row, so a reader knows why a call has no telephone number.
    source: z.enum(['numonix', 'upload']),
    date: z.string().nullable(),
    dateLabel: z.string().nullable(),
    durationMin: z.number().nullable(),
    subject: z.string().nullable(),
    sentimentScore: z.number().nullable(),
    sentimentLabel: z.string().nullable(),
    aiSummary: z.string().nullable(),
    complianceNotes: z.string().nullable(),
    topicDistribution: z.array(z.object({ topic: z.string(), pct: z.number() })),
    // The row holds no transcript (SEP-358). The list would otherwise send
    // each transcript of the book. `getCallCitationSources` gives the lines of
    // one call, and `searchCallTranscripts` finds the calls whose transcript
    // holds a text.
    keyInsights: z.array(z.string()),
    opportunityFlags: z.array(z.string()),
    complianceFlags: z.array(z.string()),
});
export type DeskCallRow = z.infer<typeof deskCallRowSchema>;

/** Reviewable lifecycle on the post-call artefact rows (ADR-0010). */
const reviewStatusSchema = z.enum(['pending', 'approved', 'rejected']).nullable();

/** An ATLAS-extracted fact (`call_extracted_facts`). */
export const deskCallFactRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    category: z.string().nullable(),
    text: z.string().nullable(),
    confidence: z.string().nullable(),
    sourceQuote: z.string().nullable(),
    accepted: z.boolean().nullable(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallFactRow = z.infer<typeof deskCallFactRowSchema>;

/** An ATLAS-extracted action item (`call_action_items`). */
export const deskCallActionRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    title: z.string().nullable(),
    owner: z.string().nullable(),
    dueDescription: z.string().nullable(),
    note: z.string().nullable(),
    completed: z.boolean().nullable(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallActionRow = z.infer<typeof deskCallActionRowSchema>;

/** An ATLAS-drafted KB note (`call_kb_notes`). `tags` is a free-form JSON
 *  array on the source row — left loose (genuinely-dynamic vendor payload,
 *  ADR-0003). */
export const deskCallNoteRowSchema = z.object({
    id: z.string(),
    callId: z.string().nullable(),
    clientId: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    template: z.string().nullable(),
    tags: z.unknown(),
    reviewStatus: reviewStatusSchema,
});
export type DeskCallNoteRow = z.infer<typeof deskCallNoteRowSchema>;

// --- Uploaded recordings (SEP-259) -----------------------------------------
// A recording lands before anybody names its client, so the ledger row is a wire
// shape of its own. It leaves the page as soon as its DeskCall exists: from that
// moment the call itself is what a reader reads, and `transcribed` therefore
// never appears in the status below.

export const deskCallUploadStatusSchema = z.enum([
    'unassigned',
    'pending',
    'transcribing',
    'failed',
]);
export type DeskCallUploadStatus = z.infer<typeof deskCallUploadStatusSchema>;

/** The three shapes a conversation takes. It sets `DeskCall.callType`. */
export const deskCallUploadCallTypeSchema = z.enum([
    'Phone call',
    'Video call',
    'In-person meeting',
]);
export type DeskCallUploadCallType = z.infer<typeof deskCallUploadCallTypeSchema>;

/** One uploaded recording, as the Desk page shows it. The bytes never ship. */
export const deskCallUploadRowSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    byteSize: z.number(),
    mediaFormat: z.string(),
    status: deskCallUploadStatusSchema,
    /** Why the row sits where it does, in one sentence, or null. */
    statusDetail: z.string().nullable(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    /** The person the recording is filed against, or null. */
    contactName: z.string().nullable(),
    callType: deskCallUploadCallTypeSchema,
    recordedAt: z.string().nullable(),
    uploadedAt: z.string(),
    uploadedByUserId: z.string(),
});
export type DeskCallUploadRow = z.infer<typeof deskCallUploadRowSchema>;

/**
 * What a person gives when they name the client of a recording.
 *
 * The multipart upload route `POST /api/desk/calls/uploads` takes the same
 * three fields as optional text fields (SEP-279). A caller that knows the
 * client, for example the onboarding case of a prospect, then files the
 * recording and starts the work in one request.
 */
export const assignCallUploadRequestSchema = z.object({
    clientId: z.string().min(1),
    /**
     * The Contact of the call (SEP-292): the person on the recording. The
     * assign step of the Desk page sends it with the Client that the pair of
     * {@link callUploadContactOptionSchema} names. The server refuses a pair
     * that is not in the option list of the reader. When it is absent, the
     * server matches the Contact from the telephone number of the Client.
     */
    contactId: z.string().min(1).optional(),
    /** The date of the conversation, as `YYYY-MM-DD`. */
    recordedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    callType: deskCallUploadCallTypeSchema,
});
export type AssignCallUploadRequest = z.infer<typeof assignCallUploadRequestSchema>;

/**
 * One person that a recording can be filed against (SEP-292), with the Client
 * that the call then belongs to.
 *
 * A call needs a Client, because the Desk visibility rule reads the Client of
 * the call. A Contact that answers for several Clients therefore gives one
 * option for each Client.
 */
export const callUploadContactOptionSchema = z.object({
    contactId: z.string(),
    contactName: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable(),
});
export type CallUploadContactOption = z.infer<typeof callUploadContactOptionSchema>;

export const listCallUploadContactsResponseSchema = z.object({
    contacts: z.array(callUploadContactOptionSchema),
});
export type ListCallUploadContactsResponse = z.infer<typeof listCallUploadContactsResponseSchema>;

// --- Recorded calls with no client --------------------------------------------
// The poller files a recorded call against a Client when the number of the far
// party is in exactly one Client. A number on several Clients, or a number that
// matched a Contact only, files nothing, and the call waits for the investment
// manager who handled it. A number that matched nothing stays out of sight. No
// transcript exists yet: transcription starts only after the call has a
// client, because each transcription is paid for.

/**
 * One choice for a waiting call: a person and a Client of the book of the
 * reader. For each matched Contact that the reader may read, the Clients that
 * hold the number and the Clients that the Contact is linked to. `contactId` is
 * null only when the reader may read no matched Contact.
 */
export const unassignedCallCandidateSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    contactId: z.string().nullable(),
    contactName: z.string().nullable(),
});
export type UnassignedCallCandidate = z.infer<typeof unassignedCallCandidateSchema>;

/**
 * One CALLER that waits for a client: the recorded calls of one far party in
 * the last 7 days, as one row. The row carries metadata only, and its fields
 * are the fields of the newest call of the caller. One choice files each call
 * of the caller that offers that choice.
 */
export const deskUnassignedCallRowSchema = z.object({
    /** The Numonix id of the newest call of the caller. */
    id: z.string(),
    /** The number of waiting calls of this caller in the window. One at least. */
    callCount: z.number().int().min(1),
    /** The Numonix ids of all the waiting calls of this caller, newest first. */
    callIds: z.array(z.string()),
    /** ISO time the call started, or null when Numonix gave none. */
    startedAt: z.string().nullable(),
    direction: z.enum(['incoming', 'outgoing']).nullable(),
    /** The number (or the address) of the far party, as Numonix gave it. */
    farPartyEndpoint: z.string().nullable(),
    /** The caller name that Numonix recorded. It never files the call. */
    callerName: z.string().nullable(),
    durationMs: z.number().nullable(),
    /** The colleague who handled the call. */
    handledByName: z.string().nullable(),
    /**
     * The choices of the caller, as pairs of a person and a Client of the
     * reader's book: each matched Contact that the reader may read, with each
     * Client it is linked to (a Relationship, or a Suitability Record and its
     * Account). With no such pair, the Clients of the book that hold the
     * number, with a null Contact. It is the union of the candidates of the
     * calls of the caller, each pair once. Never empty: a caller with no
     * candidate is not listed.
     */
    candidates: z.array(unassignedCallCandidateSchema),
});
export type DeskUnassignedCallRow = z.infer<typeof deskUnassignedCallRowSchema>;

export const listUnassignedCallsResponseSchema = z.object({
    calls: z.array(deskUnassignedCallRowSchema),
});
export type ListUnassignedCallsResponse = z.infer<typeof listUnassignedCallsResponseSchema>;

/**
 * The client that a person names for a waiting call, and the person on it. The
 * pair must be one of the `candidates` of the call exactly (`contactId` absent
 * for a candidate with no person). The server refuses any other choice and
 * stores exactly the one it accepts. The server also files each other waiting
 * call of the same caller in the window that offers the same pair.
 */
export const assignUnassignedCallRequestSchema = z.object({
    clientId: z.string().min(1),
    contactId: z.string().min(1).optional(),
});
export type AssignUnassignedCallRequest = z.infer<typeof assignUnassignedCallRequestSchema>;

/**
 * Name the firm's side of one call by hand (SEP-260).
 *
 * The label must be one the call's own transcript carries. The server refuses
 * anything else: a label that matches no line would show every line as the far
 * party, which is a worse state than the unnamed one this control exists to
 * leave behind.
 */
export const setCallFirmSpeakerRequestSchema = z.object({
    firmSpeakerLabel: z.string().min(1),
});
export type SetCallFirmSpeakerRequest = z.infer<typeof setCallFirmSpeakerRequestSchema>;

export const deskCallsMeetingsResponseSchema = z.object({
    calls: z.array(deskCallRowSchema),
    /** The recordings that have not become calls yet. */
    uploads: z.array(deskCallUploadRowSchema),
    factsByCallId: z.record(z.array(deskCallFactRowSchema)),
    actionsByCallId: z.record(z.array(deskCallActionRowSchema)),
    notesByCallId: z.record(z.array(deskCallNoteRowSchema)),
});
export type DeskCallsMeetingsResponse = z.infer<typeof deskCallsMeetingsResponseSchema>;

/**
 * The shortest text that the transcript search takes. A shorter text matches
 * almost each call, and the answer then holds almost each transcript.
 */
export const CALL_TRANSCRIPT_SEARCH_MIN_LENGTH = 3;

// --- Call citation sources --------------------------------------------------
// ATLAS KB-note / AI-summary bodies are markdown that grounds claims with
// inline footnote markers `[^N]`, closed by a `[^N]: <transcript-line-id>`
// definition block (line ids like `bf1836a1-l2`). This endpoint returns the
// call's transcript lines so the UI can resolve each marker to the verbatim
// quote (speaker + timestamp) it cites — the line id alone is meaningless to a
// reader.
export const callCitationSourceSchema = z.object({
    /** Transcript line id, e.g. `bf1836a1-l2` — the `[^N]:` definition target. */
    id: z.string(),
    speaker: z.string(),
    /** Display offset within the call, e.g. `0:13`. */
    timestamp: z.string(),
    text: z.string(),
});
export type CallCitationSource = z.infer<typeof callCitationSourceSchema>;

export const callCitationSourcesResponseSchema = z.object({
    sources: z.array(callCitationSourceSchema),
});
export type CallCitationSourcesResponse = z.infer<typeof callCitationSourcesResponseSchema>;

// --- Call transcript search -------------------------------------------------
// The Calls & Meetings search finds a text in the transcripts on the server,
// because the list holds no transcript (SEP-358). The answer holds the first
// line that matched in each call of the scope of the reader.
export const searchCallTranscriptsQuerySchema = z.object({
    /** The text to find, case-insensitive. */
    q: z.string().trim().min(CALL_TRANSCRIPT_SEARCH_MIN_LENGTH).max(200),
});

export const callTranscriptMatchSchema = z.object({
    callId: z.string(),
    /** The id of the first line that holds the text. */
    lineId: z.string(),
    /** The text of that line. */
    text: z.string(),
});
export type CallTranscriptMatch = z.infer<typeof callTranscriptMatchSchema>;

export const searchCallTranscriptsResponseSchema = z.object({
    matches: z.array(callTranscriptMatchSchema),
});
export type SearchCallTranscriptsResponse = z.infer<typeof searchCallTranscriptsResponseSchema>;

// --- Ops audit log ----------------------------------------------------------

export const opsAuditLogEntrySchema = z.object({
    id: z.string(),
    at: z.string().optional(),
    kind: z.string(),
    summary: z.string().optional(),
    // Audit-event metadata is heterogeneous per event kind (the action key
    // determines the shape) — a genuinely-dynamic payload (ADR-0003).
    meta: z.record(z.unknown()).optional(),
});
export type OpsAuditLogEntryDto = z.infer<typeof opsAuditLogEntrySchema>;

export const opsAuditLogResponseSchema = z.object({
    entries: z.array(opsAuditLogEntrySchema),
});
export type OpsAuditLogResponse = z.infer<typeof opsAuditLogResponseSchema>;

// Mandate catalogue (issue #212) — the firm's standard mandate offerings,
// surfaced on /desk/suitability below the review queue. Presentation
// projection of the MandateCatalogueEntry Object Type: `riskGradeLabel` is the
// controller-derived "{min}–{max}" display string so the UI stays dumb.
export const mandateCatalogueEntrySchema = z.object({
    id: z.string(),
    mandateType: z.enum(['Growth', 'Balanced', 'Cautious', 'Income', 'Ethical']),
    name: z.string(),
    riskGradeMin: z.number().int(),
    riskGradeMax: z.number().int(),
    riskGradeLabel: z.string(),
    targetReturnLabel: z.string(),
    benchmark: z.string(),
    displayOrder: z.number().int().nullable(),
    notes: z.string().nullable(),
});
export type MandateCatalogueEntryDto = z.infer<typeof mandateCatalogueEntrySchema>;

export const mandateCatalogueResponseSchema = z.object({
    entries: z.array(mandateCatalogueEntrySchema),
});
export type MandateCatalogueResponse = z.infer<typeof mandateCatalogueResponseSchema>;

// Portfolio holdings — the IM's book, sourced from the ValuationGrid
// (`client_holdings`) scoped to the clients they manage and aggregated by ISIN
// in the controller. The grid carries the custodian valuation as at its value
// date, so unlike the value-less custodian recon feeds this projection DOES
// carry `valuationGbp` (real, not fabricated — ADR-0009). `portfolioCount` is
// how many of the IM's portfolios hold the ISIN. The OpenFIGI monogram resolves
// from `isin` + `securityName`.
export const deskPortfolioHoldingSchema = z.object({
    isin: z.string(),
    securityName: z.string(),
    // Sum of quantity across every portfolio of the IM's clients holding this
    // ISIN. Unit-agnostic (shares / nominal).
    quantity: z.number(),
    // Sum of the grid valuation (£) across those holdings.
    valuationGbp: z.number(),
    // Number of distinct portfolios (client accounts) holding this ISIN.
    portfolioCount: z.number(),
});
export type DeskPortfolioHoldingDto = z.infer<typeof deskPortfolioHoldingSchema>;

export const deskPortfolioHoldingsResponseSchema = z.object({
    holdings: z.array(deskPortfolioHoldingSchema),
});
export type DeskPortfolioHoldingsResponse = z.infer<typeof deskPortfolioHoldingsResponseSchema>;

// Holders of a single aggregated ISIN — the drill-down behind a Portfolio row.
// One entry per client (of the IM's book) holding the security, each carrying
// the individual accounts (portfolios) that hold it and the size of each
// position (quantity + £ valuation). Discretionary (AUM) only, so the summed
// child sizes reconcile with the parent aggregate row. `accountRef` is the
// grid portfolio ref (== `portfolios.tercero_account_ref`).
export const deskPortfolioHolderAccountSchema = z.object({
    accountRef: z.string(),
    accountLabel: z.string(),
    mandateType: z.string().nullable(),
    quantity: z.number(),
    valuationGbp: z.number(),
    // Summed acquisition cost (£) of the position — the ingested
    // `client_holdings.book_cost_gbp`, carried straight through, never derived.
    // The grid writes 0 where it holds no acquisition cost, so 0 reads as
    // "not supplied" rather than "free"; the drill-down renders it as a dash.
    // Drill-down only: the parent per-ISIN aggregate row does not carry it.
    bookCostGbp: z.number(),
});
export type DeskPortfolioHolderAccount = z.infer<typeof deskPortfolioHolderAccountSchema>;

export const deskPortfolioHolderSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    accounts: z.array(deskPortfolioHolderAccountSchema),
});
export type DeskPortfolioHolder = z.infer<typeof deskPortfolioHolderSchema>;

export const deskPortfolioHoldingHoldersResponseSchema = z.object({
    holders: z.array(deskPortfolioHolderSchema),
});
export type DeskPortfolioHoldingHoldersResponse = z.infer<
    typeof deskPortfolioHoldingHoldersResponseSchema
>;

// --- FeedItem lifecycle (Ops Command Centre) --------------------------------
// The three lifecycle mutations dispatch through the FeedItem Object Type's
// Actions (ADR-0002) and echo the updated row. The row is a real DTO (the
// server reads it as a typed FeedItem) — not the generic action-result `data`
// envelope.

export const feedItemDtoSchema = z.object({
    id: z.string(),
    timestamp: z.string().nullable(),
    category: z.string().nullable(),
    severity: z.string().nullable(),
    title: z.string().nullable(),
    detail: z.string().nullable(),
    aiAssessment: z.string().nullable(),
    proposedAction: z.string().nullable(),
    status: z.enum(['New', 'In Progress', 'Resolved']),
});
export type FeedItemDto = z.infer<typeof feedItemDtoSchema>;

/** Staff-action OK envelope specialised to carry the updated FeedItem row. */
export const feedItemActionOkSchema = z.object({
    ok: z.literal(true),
    data: z.object({ feedItem: feedItemDtoSchema }),
    invalidate: z.array(z.string()).optional(),
});
export type FeedItemActionOk = z.infer<typeof feedItemActionOkSchema>;

export const deskPagesContract = c.router(
    {
        getClientKnowledge: {
            method: 'GET',
            path: '/desk/client-knowledge',
            // The two knowledge maps hold the knowledge of `contactId` only, and
            // they are empty with no `contactId` (SEP-358). The picker and the
            // map from a Client to its contact always hold each contact.
            query: z.object({ contactId: z.string().min(1).optional() }),
            responses: { 200: deskClientKnowledgeResponseSchema, 403: errorBody },
            summary: 'Desk > Client Knowledge reference data',
        },
        // Attach an IM-authored Markdown document to a PERSON's knowledge base.
        // Dispatches the AddClientKnowledgeDocument Action (ADR-0002) and echoes
        // the created document as a DTO. The subject is a Contact since SEP-215;
        // the owner is controller-resolved and is never read from this body.
        addClientKnowledgeDocument: {
            method: 'POST',
            path: '/desk/client-knowledge/documents',
            body: z.object({
                contactId: z.string().min(1),
                title: z.string().min(1),
                markdownContent: z.string().min(1),
                sourceFilename: z.string().optional(),
            }),
            responses: {
                200: z.object({ document: clientKnowledgeDocumentSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Add an IM-authored Markdown document to a client knowledge base',
        },
        // Soft-delete an IM-authored Markdown document (only the manual ones —
        // ATLAS artefacts are immutable here). Dispatches the
        // DeleteClientKnowledgeDocument Action (ADR-0002).
        deleteClientKnowledgeDocument: {
            method: 'POST',
            path: '/desk/client-knowledge/documents/:id/delete',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Soft-delete an IM-authored client knowledge document',
        },
        // Propose a person-to-person relationship by hand. It dispatches the
        // CreateRelationshipProposal Action (ADR-0002) and echoes the created
        // proposal as a DTO. `proposedRole` is a RelationshipRole catalogue key
        // with appliesTo 'contact_contact'; the Action refuses an unknown key.
        // The owner is controller-resolved and is never read from this body.
        addRelationshipProposal: {
            method: 'POST',
            path: '/desk/client-knowledge/relationship-proposals',
            body: z.object({
                contactId: z.string().min(1),
                personName: z.string().min(1),
                proposedRole: z.string().min(1),
                proposedRoleLabel: z.string().optional(),
                note: z.string().optional(),
            }),
            responses: {
                200: z.object({ proposal: relationshipProposalSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Propose a person-to-person relationship on a client knowledge graph',
        },
        // --- Knowledge assignment (SEP-183 round two) -----------------------
        // The review thumb is not the only moment a manager elects the records
        // an insight applies to. A person answers for many records, and which
        // one a conversation was about is often clear only afterwards. These
        // two doors carry that later decision.
        //
        // Both dispatch the ontology Actions (ADR-0002), so the cross-owner
        // refusal (CROSS_OWNER_ASSIGNMENT) and the Desk row scope apply exactly
        // as they do at the thumb. Neither door takes an owner from its body.
        assignKnowledgeItem: {
            method: 'POST',
            path: '/desk/client-knowledge/assignments',
            body: z.object({
                itemType: knowledgeItemTypeSchema,
                itemId: z.string().min(1),
                targetType: knowledgeAssignmentTargetTypeSchema,
                targetId: z.string().min(1),
                note: z.string().optional(),
            }),
            responses: {
                // `ok` carries the success, because a caller reads this body
                // through `runStaffEndpoint`, and that helper returns the 200
                // body itself as the `StaffActionResult`. A body with no `ok`
                // reads as a failure with no message: the row is written, and
                // the page neither refreshes nor says why. `created` is false
                // when the reference already exists, because the Action is
                // idempotent and a double click writes nothing new.
                200: z.object({ ok: z.literal(true), id: z.string(), created: z.boolean() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Assign one knowledge entry to a Suitability Record or a Client',
        },
        // End a reference. Nothing is hard-deleted: the row keeps its dates and
        // stays readable, the way an ended Relationship does (ADR-0015).
        endKnowledgeAssignment: {
            method: 'POST',
            path: '/desk/client-knowledge/assignments/:id/end',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'End a knowledge assignment',
        },
        // The two bands a record renders (SEP-222). The door accepts both
        // target tiers. Only the Suitability Record carries a detail page
        // today, so only that tier calls it; the Account tier needs no
        // server work when the tier gains a page.
        getRecordKnowledge: {
            method: 'GET',
            path: '/desk/records/:targetType/:targetId/knowledge',
            pathParams: z.object({
                targetType: knowledgeAssignmentTargetTypeSchema,
                targetId: z.string(),
            }),
            responses: { 200: recordKnowledgeResponseSchema, 403: errorBody, 404: errorBody },
            summary: 'The knowledge a record inherits and the knowledge it carries',
        },
        // There is no delete endpoint. SEP-215 retired `deleteClientConnection`:
        // it had no caller on the frontend, and review is the only door out of a
        // proposal. A reviewer rejects a proposal with a stated reason through
        // the agent review queue, and the row stays for audit.
        getCallsMeetings: {
            method: 'GET',
            path: '/desk/calls-meetings',
            responses: { 200: deskCallsMeetingsResponseSchema, 403: errorBody },
            summary: 'Desk > Calls & Meetings composed payload',
        },
        // The multipart upload itself is a RAW Nest route, because ts-rest is
        // JSON only. It is `POST /api/desk/calls/uploads`, and the two JSON
        // steps beside it are here.
        setCallFirmSpeaker: {
            method: 'POST',
            path: '/desk/calls/:id/firm-speaker',
            pathParams: z.object({ id: z.string() }),
            body: setCallFirmSpeakerRequestSchema,
            responses: {
                200: z.object({ call: deskCallRowSchema }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: "Name which side of a call's transcript is the firm",
        },
        listCallUploadContacts: {
            method: 'GET',
            path: '/desk/calls/uploads/contacts',
            responses: { 200: listCallUploadContactsResponseSchema, 403: errorBody },
            summary: 'The contacts the reader can file an uploaded recording against',
        },
        assignCallUpload: {
            method: 'POST',
            path: '/desk/calls/uploads/:id/assign',
            pathParams: z.object({ id: z.string() }),
            body: assignCallUploadRequestSchema,
            responses: {
                200: z.object({ upload: deskCallUploadRowSchema }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Name the contact and the client of an uploaded recording, and start the work',
        },
        listUnassignedCalls: {
            method: 'GET',
            path: '/desk/calls/unassigned',
            responses: { 200: listUnassignedCallsResponseSchema, 403: errorBody },
            summary: 'The recorded calls of the reader that wait for a client',
        },
        assignUnassignedCall: {
            method: 'POST',
            path: '/desk/calls/unassigned/:id/assign',
            pathParams: z.object({ id: z.string() }),
            body: assignUnassignedCallRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Name the client of a recorded call and of the other calls of its caller, and queue their transcription',
        },
        discardCallUpload: {
            method: 'DELETE',
            path: '/desk/calls/uploads/:id',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                // A recording the processor holds cannot be discarded: the
                // DeskCall of that attempt would then have no row behind it.
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Discard an uploaded recording and its bytes',
        },
        searchCallTranscripts: {
            method: 'GET',
            path: '/desk/calls-meetings/transcript-search',
            query: searchCallTranscriptsQuerySchema,
            responses: {
                200: searchCallTranscriptsResponseSchema,
                400: errorBody,
                403: errorBody,
            },
            summary: 'Find the calls whose transcript holds a text, in the scope of the reader',
        },
        getCallCitationSources: {
            method: 'GET',
            path: '/desk/calls/:callId/citation-sources',
            pathParams: z.object({ callId: z.string() }),
            responses: { 200: callCitationSourcesResponseSchema, 403: errorBody },
            summary: "Transcript-line sources for resolving a call summary's [^N] citations",
        },
        getMandateCatalogue: {
            method: 'GET',
            path: '/desk/suitability/mandate-catalogue',
            responses: { 200: mandateCatalogueResponseSchema, 403: errorBody },
            summary: 'Desk > Suitability mandate catalogue (firm offerings)',
        },
        getPortfolioHoldings: {
            method: 'GET',
            path: '/desk/portfolio/holdings',
            responses: { 200: deskPortfolioHoldingsResponseSchema, 403: errorBody },
            summary: 'Desk > Portfolio aggregate holdings (firm book, value-less)',
        },
        getPortfolioHoldingHolders: {
            method: 'GET',
            path: '/desk/portfolio/holdings/:isin/holders',
            pathParams: z.object({ isin: z.string() }),
            responses: { 200: deskPortfolioHoldingHoldersResponseSchema, 403: errorBody },
            summary: 'Desk > Portfolio holding drill-down — which clients/accounts hold this ISIN',
        },
        getOpsAuditLog: {
            method: 'GET',
            path: '/ops/audit-log',
            responses: { 200: opsAuditLogResponseSchema, 403: errorBody },
            summary: 'Ops audit log entries (admin / view persona)',
        },

        // -----------------------------------------------------------------
        // Feed-items lifecycle (OLD_CODE.html #2 replacement for the
        // desk-resolve-action / desk-snooze-action / desk-escalate-action
        // named handlers). Each dispatches through the FeedItem Object Type's
        // lifecycle Action (ADR-0002) and echoes the updated row.
        // -----------------------------------------------------------------
        resolveFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/resolve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Mark a feed-item Resolved.',
        },
        snoozeFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/snooze',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ days: z.number().int().positive().optional() }).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Snooze a feed-item (default 7 days).',
        },
        escalateFeedItem: {
            method: 'POST',
            path: '/desk/feed-items/:id/escalate',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: feedItemActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Escalate a feed-item to compliance.',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
